import * as React from 'react';
import { ValidatorForm, TextValidator, SelectValidator } from 'react-material-ui-form-validator';
import { MenuItem, Grid, Button, Collapse, Box } from '@mui/material';
import { usePaymentContext, BillingAddressDetails } from '../context';
import { useRequestManager } from '../services';

type State = BillingAddressDetails & {
    postCodeValidation: string[]
}

const defaultState: State = {
    postCodeValidation: [],
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    town: '',
    county: '',
    postCode: '',
    country: '',
    onFailureReturnUrl: '',
    onSuccessReturnUrl: ''
}

export function CaptureBillingAddress() {
    const [state, setState] = React.useState(defaultState);
    const paymentContext = usePaymentContext();
    const requestManager = useRequestManager();

    React.useEffect(() => {
        setState(prev => ({ ...prev, ...paymentContext.billingAddressDetails }));
    }, []);

    React.useEffect(() => {
        const postCodeValidation = getPostcodeValidation(state.country);
        setState(prev => ({ ...prev, postCodeValidation }));
    }, [state.country]);

    const getPostcodeValidation = (country: string) => {
        switch (country) {
            case 'IE':
                return ['required', 'matchRegexp:^[AC-Y]{1}[0-9]{1}[0-9W]{1}[ \-]?[0-9AC-Y]{4}$']
            default:
                return ['required', 'matchRegexp:^([A-Z][A-HJ-Y]?[0-9][A-Z0-9]? ?[0-9][A-Z]{2}|[G][I][R] ?0[A]{2})$']; // GB
        }
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newValue = e.target.value;
        if (e.target.name === 'postCode')
            newValue = newValue.toUpperCase();

        setState(prev => ({ ...prev, [e.target.name]: newValue }));
    }

    const onSubmit = (e?: React.FormEvent<Element>) => {
        e?.preventDefault();
        requestManager.createTransaction(state);
    }

    return <ValidatorForm onSubmit={onSubmit} noValidate autoComplete="off">
        <Box pb={5}>
            <Grid container spacing={5}>
                <Grid size={{ xs: 12, md: 6 }} >
                    <SelectValidator
                        name="country"
                        label="Country"
                        value={state.country}
                        onChange={onChange}
                        required={true}
                        validators={['required']}
                        errorMessages={['County is required']}
                        fullWidth={true}>
                        <MenuItem value="" style={{ display: 'none' }}></MenuItem>
                        <MenuItem value="GB">Great Britain</MenuItem>
                        <MenuItem value="IE">Ireland</MenuItem>
                    </SelectValidator>
                </Grid>
            </Grid>
        </Box>

        <Collapse in={Boolean(state.country)}>
            <Grid container spacing={5}>
                <Grid size={{ xs: 12, md: 6 }} >
                    <TextValidator
                        name="firstName"
                        label="First name"
                        value={state.firstName}
                        onChange={onChange}
                        required={true}
                        validators={['required', 'maxStringLength:20']}
                        errorMessages={['First name is required', 'First name is too long']}
                        fullWidth={true} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }} >
                    <TextValidator
                        name="lastName"
                        label="Last name"
                        value={state.lastName}
                        onChange={onChange}
                        required={true}
                        validators={['required', 'maxStringLength:20']}
                        errorMessages={['Last name is required', 'Last name is too long']}
                        fullWidth={true} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }} >
                    <TextValidator
                        name="addressLine1"
                        label="Address line 1"
                        value={state.addressLine1}
                        onChange={onChange}
                        required={true}
                        validators={['required', 'maxStringLength:100']}
                        errorMessages={['Address line 1 is required', 'Address line 1 is too long']}
                        fullWidth={true} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }} >
                    <TextValidator
                        name="addressLine2"
                        label="Address line 2"
                        value={state.addressLine2}
                        onChange={onChange}
                        required={false}
                        validators={['maxStringLength:100']}
                        errorMessages={['Address line 1 is too long']}
                        fullWidth={true} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }} >
                    <TextValidator
                        name="town"
                        label="Town / City"
                        value={state.town}
                        onChange={onChange}
                        required={true}
                        validators={['required', 'maxStringLength:40']}
                        errorMessages={['Town / City is required', 'Town / City is too long']}
                        fullWidth={true} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }} >
                    <TextValidator
                        name="county"
                        label="County"
                        value={state.county}
                        onChange={onChange}
                        required={true}
                        validators={['required', 'maxStringLength:40']}
                        errorMessages={['County is required', 'County is too long']}
                        fullWidth={true} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }} >
                    <TextValidator
                        name="postCode"
                        label="Postcode"
                        value={state.postCode}
                        onChange={onChange}
                        required={true}
                        validators={state.postCodeValidation}
                        errorMessages={['Post code is required', 'Post code is invalid']}
                        fullWidth={true} />
                </Grid>

                <Box width={1}>
                    <Box display="flex" alignItems="flex-start">
                        <Box display="flex" flexGrow="1" />

                        <Button
                            variant="contained"
                            size="large"
                            type="submit"
                            color="primary">Next</Button>
                    </Box>
                </Box>
            </Grid>
        </Collapse>
    </ValidatorForm>;
}

export default CaptureBillingAddress;